import { getDataAPI } from '../utils/fetchData';

/**
 * Charting Service
 * Handles all charting-related API calls using SongStats data
 */
class ChartingService {
  
  /**
   * Get chart activities for all artists in user's catalog
   * @param {Object} options - Query options
   * @param {string} options.activity_type - Filter by activity type (e.g., 'chart')
   * @param {string} options.time_period - Time period for data (e.g., '30d', '7d')
   * @param {number} options.max_artists - Maximum number of artists to process
   * @param {number} options.limit - Maximum activities per artist
   * @returns {Promise} Chart activities data
   */
  async getCatalogArtistActivities(options = {}) {
    try {
      const {
        activity_type = 'chart',
        time_period = '30d', 
        max_artists = 10,
        limit = 100  // Increased from 20 to 100
      } = options;

      console.log('🎯 ChartingService: Getting catalog artist activities with options:', options);

      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      if (!token) {
        console.error('❌ ChartingService: No authentication token found');
        throw new Error('No authentication token found');
      }

      console.log('🔑 ChartingService: Token exists, making API call...');

      const queryParams = new URLSearchParams({
        activity_type,
        time_period,
        max_artists: max_artists.toString(),
        limit: limit.toString()
      }).toString();

      const apiUrl = `user/songstats/catalog/artist_activities?${queryParams}`;
      console.log('🌐 ChartingService: API URL:', apiUrl);

      const response = await getDataAPI(apiUrl, token);
      
      console.log('📡 ChartingService: Raw API response:', response);
      console.log('📡 ChartingService: Response status:', response?.data?.status);
      console.log('📡 ChartingService: Response data keys:', response?.data?.data ? Object.keys(response.data.data) : 'No data');

      if (response?.data?.status === 200) {
        console.log('✅ ChartingService: Successfully fetched catalog artist activities');
        console.log('📊 ChartingService: Activities count:', response.data.data?.all_activities?.length || 0);
        return response.data.data;
      } else {
        console.warn('⚠️ ChartingService: Unexpected response from catalog artist activities API');
        console.warn('⚠️ ChartingService: Response:', response);
        return {
          artists: [],
          all_activities: [],
          summary: { total_artists: 0, total_activities: 0 }
        };
      }

    } catch (error) {
      console.error('❌ ChartingService: Error fetching catalog artist activities:', error);
      console.error('❌ ChartingService: Error message:', error.message);
      if (error.response) {
        console.error('❌ ChartingService: Error response status:', error.response.status);
        console.error('❌ ChartingService: Error response data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Get chart activities for a specific artist
   * @param {string} spotifyArtistId - Spotify artist ID
   * @param {Object} options - Query options  
   * @returns {Promise} Artist chart activities data
   */
  async getArtistActivities(spotifyArtistId, options = {}) {
    try {
      const {
        activity_type = 'chart',
        time_period = '30d',
        limit = 50
      } = options;

      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('📊 Fetching artist activities for:', spotifyArtistId);
      console.log('📊 Options:', options);

      const queryParams = new URLSearchParams({
        spotify_artist_id: spotifyArtistId,
        activity_type,
        time_period,
        limit: limit.toString()
      }).toString();

      const apiUrl = `user/songstats/artists/activities-v2?${queryParams}`;
      console.log('🌐 Full API URL:', apiUrl);

      const response = await getDataAPI(apiUrl, token);
      
      console.log('📡 Full API response for artist activities:', response);
      console.log('📡 Response status:', response?.data?.status);
      console.log('📡 Response data structure:', response?.data?.data);
      console.log('📡 Activities array length:', response?.data?.data?.activities?.length || 'No activities array');

      if (response?.data?.status === 200) {
        console.log('✅ Successfully fetched artist activities');
        const responseData = response.data.data;
        
        // Enhanced logging of the response structure
        if (responseData && typeof responseData === 'object') {
          console.log('📊 Response data keys:', Object.keys(responseData));
          
          if (responseData.activities && Array.isArray(responseData.activities)) {
            console.log('📊 Found activities array with', responseData.activities.length, 'items');
            if (responseData.activities.length > 0) {
              console.log('📊 First activity sample:', responseData.activities[0]);
              console.log('📊 Activity types in response:', [...new Set(responseData.activities.map(a => a.activity_type))]);
            }
          } else {
            console.warn('⚠️ No activities array found in response data');
          }
        } else {
          console.warn('⚠️ Response data is not an object:', typeof responseData);
        }
        
        return responseData;
      } else {
        console.warn('⚠️ Unexpected response from artist activities API');
        console.warn('⚠️ Status:', response?.data?.status);
        console.warn('⚠️ Full response:', response);
        return { activities: [] };
      }

    } catch (error) {
      console.error('❌ Error fetching artist activities:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  /**
   * Process chart activities data into standardized format
   * @param {Object} activitiesData - Raw activities data from API
   * @returns {Array} Processed chart data
   */
  processChartActivities(activitiesData) {
    if (!activitiesData || !activitiesData.all_activities) {
      return [];
    }

    const { all_activities } = activitiesData;
    
    console.log('🔍 Processing activities:', all_activities.length);
    console.log('🔍 First activity sample:', all_activities[0]);
    console.log('🔍 Activity types found:', [...new Set(all_activities.map(a => a.activity_type))]);
    
    return all_activities
      .filter(activity => {
        const activityType = activity.activity_type || activity.type || '';
        const activityText = activity.activity_text || '';
        
        // Only include actual chart activities (exclude playlist, comment, video, etc.)
        const isChart = activityType === 'track_chart' ||
                       activityType === 'album_chart' ||
                       activityType === 'chart_entry' ||
                       activityType === 'chart_position' ||
                       activityType.toLowerCase().includes('chart') ||
                       activityText.toLowerCase().includes('chart') ||
                       activityText.toLowerCase().includes('position') ||
                       activityText.toLowerCase().includes('reached #') ||
                       activityText.toLowerCase().includes('peaked at #') ||
                       (activity.chart_name && activity.chart_name.length > 0);
        
        // Log what we're including/excluding
        if (isChart) {
          console.log('✅ Including chart activity:', activityType, '-', activityText.substring(0, 80) + '...');
        } else {
          console.log('🚫 Excluding non-chart activity:', activityType, '-', activityText.substring(0, 50) + '...');
        }
        
        return isChart;
      })
      .map(activity => {
        const artistInfo = activity.artist_info || {};
        const trackInfo = activity.track_info || {};
        
        console.log('🔍 Mapping activity structure:', {
          activity_type: activity.activity_type,
          activity_text: activity.activity_text?.substring(0, 50) + '...',
          source: activity.source,
          has_track_info: !!activity.track_info,
          activity_tier: activity.activity_tier
        });
        
        // Extract real chart position from activity text
        const extractChartPosition = (text) => {
          if (!text) return null;
          
          // Look for patterns like "#4", "position 4", "peaked at #15", "reached #26", etc.
          const patterns = [
            /#(\d+)/g,                          // #4, #15, #26
            /position\s+(\d+)/gi,               // position 4
            /peaked\s+at\s+#?(\d+)/gi,          // peaked at #15
            /reached\s+#?(\d+)/gi,              // reached #26
            /charted\s+#?(\d+)/gi,              // charted #4
            /hit\s+#?(\d+)/gi,                  // hit #10
            /climbed\s+to\s+#?(\d+)/gi,         // climbed to #5
            /entered\s+at\s+#?(\d+)/gi          // entered at #12
          ];
          
          for (const pattern of patterns) {
            const matches = [...text.matchAll(pattern)];
            if (matches.length > 0) {
              return parseInt(matches[0][1]);
            }
          }
          
          return null;
        };
        
        const activityText = activity.activity_text || '';
        const extractedPosition = extractChartPosition(activityText);
        const displayPosition = extractedPosition || activity.position || activity.chart_position || activity.current_position || activity.activity_tier;
        
        return {
          id: activity.id || activity.chart_id || activity.activity_id,
          // For SongStats: use activity_text as the main description  
          chart_name: activity.chart_name || activity.activity_text || activity.name || 'SongStats Activity',
          country: activity.country || activity.region || 'Global',
          position: displayPosition,
          track_name: activity.track_name || activity.song_name || trackInfo.title || activity.title,
          artist_name: activity.artist_name || activity.artist || artistInfo.artist_name || trackInfo.artists?.[0]?.name,
          previous_position: activity.previous_position || activity.last_position,
          weeks_on_chart: activity.weeks_on_chart || activity.chart_weeks,
          peak_position: activity.peak_position || activity.best_position,
          platform: this.determinePlatform(activity.source || activity.platform || activity.chart_source),
          date: activity.date || activity.activity_date || activity.created_at || activity.chart_date,
          artist_tracks_count: artistInfo.tracks_count || 1,
          data_source: 'songstats_catalog',
          activity_type: activity.activity_type,
          // SongStats specific fields
          activity_text: activity.activity_text,
          activity_url: activity.activity_url,
          activity_tier: activity.activity_tier,
          // Debug info
          extracted_position: extractedPosition,
          original_position: activity.position
        };
      })
      .filter(item => {
        // More relaxed filter - only require basic chart information
        return item.chart_name && item.artist_name;
      })
      .sort((a, b) => {
        // Sort by date first (most recent first), then by position
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB.getTime() - dateA.getTime(); // Most recent first
        }
        
        // If dates are the same, sort by position (best position first)
        return (a.position || 999) - (b.position || 999);
      });
  }

  /**
   * Determine platform from source string
   * @param {string} source - Source platform string
   * @returns {string} Standardized platform name
   */
  determinePlatform(source) {
    if (!source) return 'other';
    
    const sourceStr = source.toLowerCase();
    
    // Match SongStats exact source IDs first
    if (sourceStr.includes('spotify')) return 'spotify';
    if (sourceStr.includes('apple_music') || sourceStr.includes('apple music')) return 'apple_music';
    if (sourceStr.includes('amazon')) return 'amazon';
    if (sourceStr.includes('deezer')) return 'deezer';
    if (sourceStr.includes('tiktok')) return 'tiktok';
    if (sourceStr.includes('youtube')) return 'youtube';
    if (sourceStr.includes('shazam')) return 'shazam';
    if (sourceStr.includes('tracklist') || sourceStr.includes('1001')) return 'tracklist';
    if (sourceStr.includes('beatport')) return 'beatport';
    if (sourceStr.includes('traxsource')) return 'traxsource';
    if (sourceStr.includes('itunes')) return 'itunes';
    if (sourceStr.includes('tidal')) return 'tidal';
    if (sourceStr.includes('soundcloud')) return 'soundcloud';
    
    // Legacy Billboard detection (not in SongStats sources but still relevant)
    if (sourceStr.includes('billboard')) return 'billboard';
    
    // Fallback patterns for Apple Music
    if (sourceStr.includes('apple')) return 'apple_music';
    
    return 'other';
  }
}

// Create and export a singleton instance
const chartingServiceInstance = new ChartingService();
export default chartingServiceInstance; 